import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import Select from "react-select";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Form,
} from "reactstrap";
import api from "service/api";
import { toast } from "react-toastify";
import { AddBox, EditNote } from "@mui/icons-material";
import { CookieTool } from "utils/CookieTool";

const CreateVocabularyLibraryModal = ({
  isOpen,
  toggle,
  selectedClient,
  onLibraryCreated,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [termsList, setTermsList] = useState([]);
  const [currentTerm, setCurrentTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAddTerm = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedTerm = currentTerm.trim();

      if (!trimmedTerm) return;

      if (termsList.includes(trimmedTerm)) {
        toast.warning("Este termo já foi adicionado");
        return;
      }

      setTermsList((prev) => [...prev, trimmedTerm]);
      setCurrentTerm("");
    }
  };

  const handleRemoveTerm = (termToRemove) => {
    setTermsList((prev) => prev.filter((term) => term !== termToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }

    if (termsList.length === 0) {
      toast.error("Adicione pelo menos um termo");
      return;
    }

    setLoading(true);

    try {
      const termsArray = termsList.map((term) => ({ term }));

      const payload = {
        title: title.trim(),
        description: description.trim(),
        type: "vocabulary",
        clientId: selectedClient.id,
        terms: termsArray,
        referencedLibraries: [],
      };

      const response = await api.post("/libraries", payload);

      const newLibrary = {
        id: response.data.id,
        title: response.data.title,
        value: response.data.id,
        label: response.data.title,
      };

      toast.success("Biblioteca de vocabulário criada com sucesso!");

      // Reset form
      setTitle("");
      setDescription("");
      setTermsList([]);
      setCurrentTerm("");
      toggle();

      onLibraryCreated(newLibrary);
    } catch (error) {
      console.error("Error creating vocabulary library:", error);
      toast.error(
        error.response?.data?.message ||
          "Erro ao criar biblioteca de vocabulário",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setTermsList([]);
    setCurrentTerm("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        Nova Biblioteca de Vocabulário
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="title">Título *</Label>
            <Input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nome da biblioteca de vocabulário"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label for="description">Descrição</Label>
            <Input
              type="textarea"
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o propósito desta biblioteca"
              rows={3}
            />
          </FormGroup>

          <FormGroup>
            <Label for="currentTerm">Adicionar Termo ao Vocabulário *</Label>
            <Input
              type="text"
              id="currentTerm"
              value={currentTerm}
              onChange={(e) => setCurrentTerm(e.target.value)}
              onKeyDown={handleAddTerm}
              placeholder="Digite um termo e pressione Enter (ex: Mercado Livre)"
            />
            <small className="text-muted">
              Digite um termo e pressione Enter para adicionar à lista.
            </small>
          </FormGroup>

          {termsList.length > 0 && (
            <FormGroup>
              <Label>Termos Adicionados ({termsList.length})</Label>
              <div
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  padding: "8px",
                  maxHeight: "350px",
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {termsList.map((term, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                      margin: "2px 0",
                      backgroundColor: "white",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                    }}
                  >
                    <span style={{ fontSize: "14px" }}>{term}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTerm(term)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "16px",
                        padding: "0 4px",
                      }}
                      title="Remover termo"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={loading || termsList.length === 0 || !title.trim()}
          >
            {loading ? "Criando..." : "Criar Biblioteca"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

const EditVocabularyLibraryModal = ({
  isOpen,
  toggle,
  selectedClient,
  library,
  onLibraryUpdated,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [termsList, setTermsList] = useState([]);
  const [currentTerm, setCurrentTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // Load library data when modal opens
  useEffect(() => {
    const loadLibraryData = async () => {
      if (!library?.id || !isOpen) return;

      setLoading(true);
      try {
        const response = await api.get(`/library/${library.id}`);
        const libraryData = response.data;

        setTitle(libraryData.title || "");
        setDescription(libraryData.description || "");
        setTermsList(libraryData.terms?.map((term) => term.term) || []);
      } catch (error) {
        console.error("Error loading library data:", error);
        toast.error("Erro ao carregar dados da biblioteca");
      } finally {
        setLoading(false);
      }
    };

    loadLibraryData();
  }, [library?.id, isOpen]);

  const handleAddTerm = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedTerm = currentTerm.trim();

      if (!trimmedTerm) return;

      if (termsList.includes(trimmedTerm)) {
        toast.warning("Este termo já foi adicionado");
        return;
      }

      setTermsList((prev) => [...prev, trimmedTerm]);
      setCurrentTerm("");
    }
  };

  const handleRemoveTerm = (termToRemove) => {
    setTermsList((prev) => prev.filter((term) => term !== termToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }

    if (termsList.length === 0) {
      toast.error("Adicione pelo menos um termo");
      return;
    }

    setLoading(true);

    try {
      const termsArray = termsList.map((term) => ({ term }));

      console.log(library.id);
      console.log(selectedClient);

      const payload = {
        title: title.trim(),
        description: description.trim(),
        type: "vocabulary",
        clientId: selectedClient.id,
        terms: termsArray,
        referencedLibraries: [],
      };

      const response = await api.put(`/libraries/${library.id}`, payload);

      const updatedLibrary = {
        id: response.data.id,
        title: response.data.title,
        value: response.data.id,
        label: response.data.title,
      };

      toast.success("Biblioteca de vocabulário atualizada com sucesso!");

      // Reset form
      setTitle("");
      setDescription("");
      setTermsList([]);
      setCurrentTerm("");
      toggle();

      onLibraryUpdated(updatedLibrary);
    } catch (error) {
      console.error("Error updating vocabulary library:", error);
      toast.error(
        error.response?.data?.message ||
          "Erro ao atualizar biblioteca de vocabulário",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setTermsList([]);
    setCurrentTerm("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        Editar Biblioteca de Vocabulário: {library?.title}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="editTitle">Título *</Label>
            <Input
              type="text"
              id="editTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nome da biblioteca de vocabulário"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label for="editDescription">Descrição</Label>
            <Input
              type="textarea"
              id="editDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o propósito desta biblioteca"
              rows={3}
            />
          </FormGroup>

          <FormGroup>
            <Label for="editCurrentTerm">
              Adicionar Termo de Vocabulário *
            </Label>
            <Input
              type="text"
              id="editCurrentTerm"
              value={currentTerm}
              onChange={(e) => setCurrentTerm(e.target.value)}
              onKeyDown={handleAddTerm}
              placeholder="Digite um termo e pressione Enter (ex: Mercado Livre)"
            />
            <small className="text-muted">
              Digite um termo e pressione Enter para adicionar à lista.
            </small>
          </FormGroup>

          {termsList.length > 0 && (
            <FormGroup>
              <Label>Termos Adicionados ({termsList.length})</Label>
              <div
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  padding: "8px",
                  maxHeight: "250px",
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {termsList.map((term, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                      margin: "2px 0",
                      backgroundColor: "white",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                    }}
                  >
                    <span style={{ fontSize: "14px" }}>{term}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTerm(term)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "16px",
                        padding: "0 4px",
                      }}
                      title="Remover termo"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={loading || termsList.length === 0 || !title.trim()}
          >
            {loading ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

const VocabularyLibrarySelect = () => {
  const { setValue, watch, clearErrors } = useFormContext();
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const selectedClient = watch("client");
  const selectedVocabularyLibrary = watch("vocabularyLibrary");
  const permissions = CookieTool.getLarge("permissions");

  const fetchLibraries = async () => {
    if (!selectedClient?.id) {
      setLibraries([]);
      return;
    }

    setLoading(true);

    try {
      const response = await api.get("/libraries", {
        params: {
          page: 1,
          limit: 100,
          clientId: selectedClient.id,
          type: "vocabulary",
        },
      });

      if (!response?.data.records) {
        setLibraries([]);
        return;
      }

      const librariesOptions = response?.data?.records?.map((item) => ({
        value: item.id,
        label: item.title,
        id: item.id,
        title: item.title,
      }));

      setLibraries(librariesOptions);
    } catch (error) {
      console.error("Error fetching vocabulary libraries:", error);
      toast.error(
        "Erro ao buscar bibliotecas de vocabulário para o cliente selecionado.",
      );
      setLibraries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLibraries();
    setValue("vocabularyLibrary", null);
  }, [selectedClient?.id, setValue]);

  const handleLibraryChange = (option) => {
    setValue(
      "vocabularyLibrary",
      option ? { id: option.id, title: option.title } : null,
    );
    clearErrors("vocabularyLibrary");
  };

  const handleCreateLibrary = () => {
    setModalOpen(true);
  };

  const handleLibraryCreated = (newLibrary) => {
    setLibraries((prev) => [...prev, newLibrary]);

    setValue("vocabularyLibrary", {
      id: newLibrary.id,
      title: newLibrary.title,
    });
    clearErrors("vocabularyLibrary");
  };

  const handleEditLibrary = () => {
    setEditModalOpen(true);
  };

  const handleLibraryUpdated = (updatedLibrary) => {
    setLibraries((prev) =>
      prev.map((lib) => (lib.id === updatedLibrary.id ? updatedLibrary : lib)),
    );

    if (selectedVocabularyLibrary?.id === updatedLibrary.id) {
      setValue("vocabularyLibrary", {
        id: updatedLibrary.id,
        title: updatedLibrary.title,
      });
    }

    fetchLibraries();
  };

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center mb-1">
        <label className="form-control-label mb-0 mr-2">
          Biblioteca de Vocabulário (Opcional)
        </label>
        <div className="d-flex">
          {permissions && permissions.includes("create:library") && (
            <AddBox
              type="button"
              color="inherit"
              fontSize="large"
              onClick={handleCreateLibrary}
              disabled={!selectedClient || !selectedClient.id}
              title="Criar nova biblioteca de vocabulário"
            />
          )}
          {permissions &&
            permissions.includes("update:library") &&
            selectedVocabularyLibrary &&
            selectedVocabularyLibrary.id && (
              <EditNote
                type="button"
                color="action"
                fontSize="large"
                onClick={handleEditLibrary}
                title="Editar biblioteca selecionada"
              />
            )}
        </div>
      </div>

      <Select
        options={libraries}
        isLoading={loading}
        placeholder={
          !selectedClient?.id
            ? "Selecione um cliente primeiro"
            : loading
              ? "Carregando bibliotecas..."
              : "Selecione uma biblioteca de vocabulário"
        }
        onChange={handleLibraryChange}
        value={
          selectedVocabularyLibrary
            ? libraries.find(
                (opt) => opt.id === selectedVocabularyLibrary.id,
              ) || {
                value: selectedVocabularyLibrary.id,
                label: selectedVocabularyLibrary.title,
                id: selectedVocabularyLibrary.id,
                title: selectedVocabularyLibrary.title,
              }
            : null
        }
        isDisabled={!selectedClient?.id || loading}
        isClearable
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base) => ({
            ...base,
            minHeight: "38px",
            borderColor: "#cad1d7",
          }),
        }}
        noOptionsMessage={() => "Nenhuma biblioteca de vocabulário encontrada."}
      />

      <small className="text-muted">
        Uma biblioteca de vocabulário pode ajudar a melhorar a precisão da
        transcrição para termos específicos do seu domínio.
      </small>

      <CreateVocabularyLibraryModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        selectedClient={selectedClient}
        onLibraryCreated={handleLibraryCreated}
      />

      <EditVocabularyLibraryModal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(!editModalOpen)}
        library={selectedVocabularyLibrary}
        onLibraryUpdated={handleLibraryUpdated}
        selectedClient={selectedClient}
      />
    </div>
  );
};

export default VocabularyLibrarySelect;
